import { ApiQueries, SCALEFACTOR_FOR_CONVERSION } from '../constant';
import { makeAutoObservable } from 'mobx';
import { Color, ProjectScope } from '../config';
import { Utils } from '../utils/Utils';
import { message } from 'antd';
import { PDFDocument } from 'pdf-lib';
import {
    CLIENT_CREATE_PROJECT_API,
    CLIENT_SIDE_SAVE_API,
    IS_PRODUCTION,
    LAYOUT_API_ENDPOINT,
    PREDICT_API,
    SERVER_BASE_URL,
} from '../__common/env';
import { getIdToken } from '../utils/getIdToken';
import { DownloadUtils } from '../utils/DownloadUtils';

export class ApiManager {
    _libState = null;
    _parentProjectId = null;
    _projectId = null;
    constructor(libState) {
        makeAutoObservable(this);
        this._libState = libState;
    }

    get parentProjectId() {
        return this._parentProjectId;
    }

    get projectId() {
        return this._projectId;
    }

    setParentProjectId(value) {
        this._parentProjectId = value;
    }

    setProjectId(value) {
        this._projectId = value;
    }

    fetchAllProjects = async () => {
        const token = await getIdToken();

        const response = await fetch(
            `${SERVER_BASE_URL}/${ApiQueries.allProjectsQuery}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );

        if (!response.ok) throw new Error('Failed to fetch projects');

        const renameData = await response.json();

        return renameData;
    };

    createProject = async () => {
        const formData = new FormData();
        formData.append(
            'file',
            this._libState.closetManager.pdfBuffer,
            'pdfFile.pdf',
        );
        await this._libState.closetManager.createSelectedPagePdfBuffer();
        const pdfFile = this._libState.closetManager.pdfBuffer;

        if (!pdfFile || !(pdfFile instanceof File)) {
            console.error('Invalid pdfFile:', pdfFile);
            throw new Error(
                'PDF file is invalid or not available. Expected a File instance.',
            );
        }

        try {
            const arrayBuffer = await pdfFile.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const pdfBytes = await pdfDoc.save();
            const blob2 = new Blob([pdfBytes], { type: 'application/pdf' });

            if (blob2.size === 0) {
                console.error('trimmed_pdf Blob is empty');
                throw new Error(
                    'Failed to create valid trimmed PDF: Blob is empty',
                );
            }

            formData.append('trimmed_pdf', blob2, 'trimmed_pdf.pdf');
            this._libState.viewManager.setFullPdf(true);
        } catch (error) {
            console.error('Error processing PDF document:', error);
            console.warn(
                'Failed to process trimmed PDF, continuing without trimmed_pdf',
            );
        }

        formData.append(
            'name',
            this._libState.viewManager.formData.projectName,
        );
        formData.append(
            'creator',
            this._libState.viewManager.formData.projectCreator,
        );
        formData.append(
            'description',
            this._libState.viewManager.formData.projectDescription,
        );

        let closets = [];
        let units = [];
        const fData = this._libState.viewManager.formData;
        let jsonArrayToDownloadForAllPages = {
            name: fData.projectName,
            id: this._libState.viewManager.projectId,
            creator: fData.projectCreator,
            description: fData.projectDescription,
            projectScope: fData.projectScope,
            customScope: fData.customScope,
            color: fData.color,
            handleStyle: fData.handleStyle,
            handleColor: fData.handleColor,
            rodColor: fData.rodColor,
            doorColor: fData.doorColor,
            drawerColor: fData.drawerColor,
            drawerStyle: fData.drawerStyle,
            doorStyle: fData.doorStyle,
            legs: fData.legs,
            shelf: fData.shelf,
            topDoors: fData.topDoors,
            bottomDoors: fData.bottomDoors,
            fileUrl: fData.pdfFile.preview,
            clientProjectId: this._libState.apiManager.projectId,
            clientParentProjectId: this._libState.apiManager.parentProjectId,
            closets: closets,
            units: units,
            matrixData: [],
        };

        const blob = new Blob(
            [JSON.stringify(jsonArrayToDownloadForAllPages)],
            {
                type: 'application/json',
            },
        );
        formData.append('closets_file', blob);
        const token = await getIdToken();
        const requestOptions = {
            method: 'POST',
            body: formData,
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        const response = await fetch(
            `${SERVER_BASE_URL}/${ApiQueries.createProjectQuery}${this._libState.viewManager.selectedPages.map((page) => page.pageNum).join(',')}`,
            requestOptions,
        );

        const result = await response.text();
        const responseData = JSON.parse(result);

        return responseData;
    };

    openProject = async () => {
        try {
            const token = await getIdToken();
            const resp = await fetch(
                `${SERVER_BASE_URL}/${ApiQueries.projectQuery}/${this._libState.viewManager.projectId}?${ApiQueries.selectPagesQuery}${this._libState.viewManager.selectedPages
                    .map((p) => p.pageNum)
                    .join(',')}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            if (!resp.ok) {
                const msg = `Failed to open project – HTTP ${resp.status}`;
                throw new Error(msg);
            }

            return await resp.json();
        } catch (error) {
            console.error('openProject error:', error);
            message.error('Unable to load project. Please try again.');
            throw error; // propagate so callers can react if needed
        }
    };

    accessData = async () => {
        try {
            if (!this._libState.viewManager.projectId) {
                return;
            }
            const token = await getIdToken();
            const myHeaders = new Headers();
            myHeaders.append('Authorization', `Bearer ${token}`);
            myHeaders.append('Content-Type', 'application/json');
            const requestOptions = {
                method: 'GET',
                headers: myHeaders,
                redirect: 'follow',
            };
            const response = await fetch(
                `${SERVER_BASE_URL}/project/${this._libState.viewManager.projectId}/page-access`,
                requestOptions,
            );
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText);
            }
            const responseData = await response.json();
            return responseData;
        } catch (error) {
            console.error('Error fetching page access data:', error);
            message.error(
                'Failed to fetch page access data. Please try again.',
            );
            throw error;
        }
    };

    updatePages = async () => {
        const formData = new FormData();
        formData.append(
            'name',
            this._libState.viewManager.formData.projectName,
        );
        await this._libState.closetManager.createSelectedPagePdfBufferFromViewManagerPdf();

        const pdfFile = this._libState.closetManager.pdfBuffer;

        if (!pdfFile || !(pdfFile instanceof File)) {
            console.error('Invalid pdfFile:', pdfFile);
            throw new Error(
                'PDF file is invalid or not available. Expected a File instance.',
            );
        }

        try {
            const arrayBuffer = await pdfFile.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const pdfBytes = await pdfDoc.save();
            const blob2 = new Blob([pdfBytes], { type: 'application/pdf' });

            if (blob2.size === 0) {
                console.error('trimmed_pdf Blob is empty');
                throw new Error(
                    'Failed to create valid trimmed PDF: Blob is empty',
                );
            }

            formData.append('trimmed_pdf', blob2, 'trimmed_pdf.pdf');
        } catch (error) {
            console.error('Error processing PDF document:', error);
            console.warn(
                'Failed to process trimmed PDF, continuing without trimmed_pdf',
            );
        }
        const token = await getIdToken();
        const requestOptions = {
            method: 'PATCH',
            body: formData,
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        try {
            const response = await fetch(
                `${SERVER_BASE_URL}/${ApiQueries.projectQuery}/${this._libState.viewManager.projectId}?${ApiQueries.selectPagesQuery}${this._libState.viewManager.selectedPages.map((page) => page.pageNum).join(',')}`,
                requestOptions,
            );
            const result = await response.text();
            const responseData = JSON.parse(result);
            return responseData;
        } catch (error) {
            console.error('Error updating pages:', error);
        }
    };

    showPossibleClosets = async () => {
        if (!this._libState.viewManager.projectId) return;
        const token = await getIdToken();
        const requestOptions = {
            method: 'GET',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
        const response = await fetch(
            `${SERVER_BASE_URL}/${ApiQueries.projectQuery}/${ApiQueries.detectPointQuery}/${this._libState.viewManager.projectId}?${ApiQueries.selectPagesQuery}${this._libState.viewManager.selectedPages.map((page) => page.pageNum).join(',')}`,
            requestOptions,
        );
        const result = await response.text();
        const responseData = JSON.parse(result);
        return responseData;
    };

    uploadFinalizedProjectData = async (blob) => {
        const formData = new FormData();
        formData.append(
            'file',
            blob,
            `${this._libState.viewManager.formData.projectName}.json`,
        );
        formData.append(
            'name',
            this._libState.viewManager.formData.projectName,
        );
        const token = await getIdToken();
        const requestOptions = {
            method: 'PATCH',
            body: formData,
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
        const response = await fetch(
            `${SERVER_BASE_URL}/${ApiQueries.projectQuery}/${this._libState.viewManager.projectId}`,
            requestOptions,
        );
        const result = await response.json();

        return { result, response };
    };

    getCurrentProjectIdData = async () => {
        const token = await getIdToken();
        const response = await fetch(
            `${SERVER_BASE_URL}/${ApiQueries.projectQuery}/${this._libState.viewManager.projectId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );
        const result = await response.text();
        const responseData = JSON.parse(result);

        return responseData;
    };
    getUsersList = async () => {
        const token = await getIdToken();
        const response = await fetch(
            `${SERVER_BASE_URL}/${ApiQueries.usersQuery}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );
        return response;
    };

    assignPageAccess = async () => {
        const token = await getIdToken();
        const myHeaders = new Headers();
        myHeaders.append('Authorization', `Bearer ${token}`);
        myHeaders.append('Content-Type', 'application/json');
        const raw = JSON.stringify(this._libState.viewManager.pageOwners);
        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: raw,
            redirect: 'follow',
        };
        const response = await fetch(
            `${SERVER_BASE_URL}/project/${this._libState.viewManager.projectId}/page-access`,
            requestOptions,
        );
        return response;
    };

    transferProjectOwnership = async (projectId, newOwnerId) => {
        const token = await getIdToken();
        const response = await fetch(
            `${SERVER_BASE_URL}/project/${projectId}/transfer`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    target_user_id: newOwnerId,
                }),
            },
        );
        return response;
    };

    getAnnotationData = async () => {
        const token = await getIdToken();
        const response = await fetch(
            `${SERVER_BASE_URL}/${ApiQueries.projectQuery}/${ApiQueries.detectAnnotationQuery}/${this._libState.viewManager.projectId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );
        const result = await response.text();
        const responseData = JSON.parse(result);

        return responseData;
    };

    clientSideCreateAPI = async () => {
        const initialFormData = this._libState.viewManager.formData;
        initialFormData.color = JSON.stringify(Color[initialFormData.color]);
        initialFormData.doorColor = JSON.stringify(
            Color[initialFormData.doorColor],
        );
        initialFormData.drawerColor = JSON.stringify(
            Color[initialFormData.drawerColor],
        );

        const formData = {
            name: initialFormData.projectName,
            versionName: new Date().toISOString().split('T')[0],
            color: Number(initialFormData.color),
            estimator: initialFormData.projectCreator,
            developer: initialFormData.projectCreator,
            projectScope:
                initialFormData.projectScope === ProjectScope.CUSTOM
                    ? initialFormData.customScope
                    : initialFormData.projectScope,
            handleStyle: initialFormData.handleStyle,
            handleColor: initialFormData.handleColor,
            rodColor: initialFormData.rodColor,
            doorColor: Number(initialFormData.doorColor),
            drawerColor: Number(initialFormData.drawerColor),
            drawerStyle: initialFormData.drawerStyle,
            doorStyle: initialFormData.doorStyle,
            legs: initialFormData.legs ? 1 : 0,
            shelf: initialFormData.shelf ? 1 : 0,
            topDoors: initialFormData.topDoors ? 1 : 0,
            bottomDoors: initialFormData.bottomDoors ? 1 : 0,
            line: 'vista',
        };
        const date = new Date();
        const versionName = `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(-2)}-${`0${date.getDate()}`.slice(-2)}`;
        formData.versionName = versionName;
        const myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/json');
        const dataToUpload = JSON.stringify({
            prod: Number(IS_PRODUCTION),
            content: formData,
        });
        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: dataToUpload,
            redirect: 'follow',
        };
        const response = await fetch(CLIENT_CREATE_PROJECT_API, requestOptions);
        const result = await response.text();
        this.setProjectId(JSON.parse(result).id);
        this.setParentProjectId(JSON.parse(result).parentProjectId);

        if (this.projectId) {
            return true;
        }
        return false;
    };

    validateAllUnits = () => {
        const matrixDataCopy = [...this._libState.matrixManager.matrixData];
        const allUnits = this._libState.closetManager.units;

        const selectedUnitNames = new Set(
            matrixDataCopy.map((row) => row.type),
        );
        const unselectedUnits = allUnits.filter(
            (unit) => !selectedUnitNames.has(unit.name),
        );

        if (unselectedUnits.length > 0) {
            const unselectedUnitNames = unselectedUnits
                .map((unit) => unit.name)
                .join(', ');
            message.error(
                `Following units are mising in Matrix- ${unselectedUnitNames}`,
            );
            return false;
        }

        return true;
    };

    clientSideSaveAPI = async () => {
        const matrixDataCopy = [...this._libState.matrixManager.matrixData];
        const allUnits = this._libState.matrixManager.units;
        const selectedUnitNames = new Set(
            matrixDataCopy.map((row) => row.type),
        );
        const unselectedUnits = allUnits.filter(
            (unit) => !selectedUnitNames.has(unit.name),
        );

        if (unselectedUnits.length > 0) {
            const unselectedUnitNames = unselectedUnits
                .map((unit) => unit.name)
                .join(', ');
            message.error(
                `Following units are missing in Matrix - ${unselectedUnitNames}`,
            );
            return false;
        }
        try {
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');

            const rawArray = [];
            const rawArray2 = []; // Stores data needed for layout generation
            const formData = this._libState.viewManager.formData;
            const allClosets = this._libState.matrixManager.closets;

            const unitNameCounters = {};
            const processedUnits = new Set(); // Prevent duplicate closet data

            for (const unit of allUnits) {
                const matchingRows = matrixDataCopy.filter(
                    (row) => row.type === unit.name,
                );
                if (!unitNameCounters[unit.name]) {
                    unitNameCounters[unit.name] = 1;
                }
                const unitId = Utils.generateUuid();

                const unitType = {
                    pk: 'unitType',
                    id: unitId,
                    dateCreated: new Date()
                        .toISOString()
                        .replace('Z', '')
                        .replace('T', ' '),
                    name: unit.name,
                    projectId: this.projectId,
                    quantity: matchingRows.length,
                };
                rawArray.push(unitType);

                for (const matchedMatrix of matchingRows) {
                    const unitNumberId = Utils.generateUuid();
                    const unitNumber =
                        matchedMatrix.unitNumber ||
                        `DEFAULT_${unit.name}_${unitNameCounters[unit.name]}`;
                    if (!matchedMatrix.unitNumber) {
                        unitNameCounters[unit.name]++;
                    }

                    const unitNumberObj = {
                        pk: 'unitNumber',
                        id: unitNumberId,
                        dateCreated: new Date()
                            .toISOString()
                            .replace('Z', '')
                            .replace('T', ' '),
                        name: unitNumber,
                        unitTypeId: unitId,
                        floor: matchedMatrix.floor,
                    };
                    rawArray.push(unitNumberObj);

                    //  Only generate closet data once per unit.name
                    if (!processedUnits.has(unit.name)) {
                        const relatedClosets = allClosets.filter(
                            (closet) => closet.unitId === unit.id,
                        );

                        for (const inputCloset of relatedClosets) {
                            const closetId = Utils.generateUuid();

                            const closet = {
                                pk: 'closet',
                                id: closetId,
                                dateCreated: new Date()
                                    .toISOString()
                                    .replace('Z', '')
                                    .replace('T', ' '),
                                name: inputCloset.name,
                                bottomDoors: formData.bottomDoors ? 1 : 0,
                                color: Number(formData.color),
                                drawerStyle: formData.drawerStyle,
                                doorStyle: formData.doorStyle,
                                handleColor: formData.handleColor,
                                handleStyle: formData.handleStyle,
                                legs: formData.legs ? 1 : 0,
                                rodColor: formData.rodColor,
                                drawerColor: Number(formData.drawerColor),
                                doorColor: Number(formData.doorColor),
                                shelf: formData.shelf ? 1 : 0,
                                topDoors: formData.topDoors ? 1 : 0,
                                line: 'vista',
                                unitNumberId: unitNumberId, // one of the unitNumber IDs
                            };

                            rawArray2.push({
                                id: closetId,
                                closetData: inputCloset,
                                formData: formData,
                            });

                            rawArray.push(closet);
                        }

                        processedUnits.add(unit.name); // Mark this unit as processed
                    }
                }
            }

            // Sort by pk type
            const pkOrder = {
                unitType: 1,
                unitNumber: 2,
                closet: 3,
            };
            rawArray.sort((a, b) => pkOrder[a.pk] - pkOrder[b.pk]);

            // Generate layout only once per closet
            for (const {
                id: closetId,
                closetData: inputCloset,
                formData,
            } of rawArray2) {
                this.generateLayout(closetId, inputCloset, formData);
            }

            // Save JSON locally
            const raw = JSON.stringify(rawArray);
            DownloadUtils.downloadJSON(raw, `closet_data_${this.projectId}`);

            // POST the data
            const dataToUpload = JSON.stringify({
                prod: Number(IS_PRODUCTION),
                content: rawArray,
            });

            const requestOptions = {
                method: 'POST',
                headers: headers,
                body: dataToUpload,
                redirect: 'follow',
            };

            const response = await fetch(CLIENT_SIDE_SAVE_API, requestOptions);
            const data = await response.json();

            if (!response.ok || data.status === 'error') {
                const errorMsg =
                    data.message || response.statusText || 'Failed to upload';
                throw new Error(errorMsg);
            }
            return true;
        } catch (e) {
            message.error(e.message);
            console.error(e);
        }
    };

    generateLayout = (id, closet, formData, isUpload = true) => {
        const scale =
            this._libState.closetManager.getScaleFactorForAnnotationDisplayByClosetId(
                closet.pageNo,
                closet.id,
                true,
            );
        this._libState.operationsManager.scaleClosets(1 / scale, [closet]);
        this._libState.operationsManager.scaleAttachments(1 / scale);
        this._libState.operationsManager.scaleObstacles(1 / scale);
        this._libState.operationsManager.scaleInternalWalls(1 / scale);
        const attachments = this._libState.closetManager.attachments.filter(
            (attachment) => attachment.closetId === closet.id,
        );

        const obstacles = this._libState.closetManager.obstacles.filter(
            (obstacle) => obstacle.closetId === closet.id,
        );
        const internalWalls = this._libState.closetManager.internalWalls.filter(
            (wall) => wall.closetId === closet.id,
        );
        const { points, transX, transY } = Utils.translateClosetToCenter(
            JSON.parse(JSON.stringify(closet.coordinates)),
        );
        let translatedAttachments = [];
        let translatedObstacles = [];
        let translatedInternalWalls = [];

        attachments.forEach((attachment) => {
            const translatedAttachmentPoints = Utils.translateAttachments(
                JSON.parse(JSON.stringify(attachment.coordinates)),
                Number(transX),
                Number(transY),
            );
            const copiedAttach = JSON.parse(JSON.stringify(attachment));
            copiedAttach._coordinates = translatedAttachmentPoints;
            translatedAttachments.push(copiedAttach);
        });

        obstacles.forEach((obstacle) => {
            const translatedObstaclePoints = Utils.translateObstacles(
                JSON.parse(JSON.stringify(obstacle.data.coordinate)),
                Number(transX),
                Number(transY),
            );
            const copiedObstacle = JSON.parse(JSON.stringify(obstacle));
            copiedObstacle._data.coordinate = translatedObstaclePoints;
            translatedObstacles.push(copiedObstacle);
        });
        internalWalls.forEach((wall) => {
            const translatedWallPoints = Utils.translateObstacles(
                JSON.parse(JSON.stringify(wall.coordinate)),
                Number(transX),
                Number(transY),
            );
            const copiedWall = JSON.parse(JSON.stringify(wall));
            copiedWall._coordinate = translatedWallPoints;
            translatedInternalWalls.push(copiedWall);
        });
        const wall = Utils.getDoorWall(translatedAttachments, points);
        const {
            updatedPoints,
            updatedObstacles: updatedObstacles2,
            updatedAttachments,
            rotateAngle,
            updatedInternalWalls: updatedInternalWalls2,
        } = Utils.rotateAndPlaceWallBelow(
            points[wall.p1],
            points[wall.p2],
            points,
            translatedAttachments,
            translatedObstacles,
            translatedInternalWalls,
        );
        const updatedPoints2 = Utils.arrangePoints(updatedPoints, wall?.p2);

        const initialWallObj = closet.coordinates.map(() => []);
        const { walls, wallObj } = this.getWalls(
            updatedPoints2,
            initialWallObj,
            updatedAttachments,
        );

        const updatedObstacles = updatedObstacles2.map((obstacle) => {
            const obstacleShape = {
                rectangle: 'Rectangular',
                circle: 'Round',
            };

            const type = obstacle._obstacleType
                ? obstacleShape[obstacle._obstacleType] ||
                  obstacle._obstacleType
                : undefined;

            return {
                id: obstacle._id,
                type: type,
                position: {
                    x: obstacle._data.coordinate.x,
                    y: obstacle._data.coordinate.y,
                    z: 0,
                },
                length:
                    type === 'Rectangular'
                        ? obstacle._data.width / SCALEFACTOR_FOR_CONVERSION
                        : (obstacle._data.radius * 2) /
                          SCALEFACTOR_FOR_CONVERSION,
                depth:
                    type === 'Rectangular'
                        ? obstacle._data.height / SCALEFACTOR_FOR_CONVERSION
                        : (obstacle._data.radius * 2) /
                          SCALEFACTOR_FOR_CONVERSION,
                rotation: obstacle._data.rotation,
                wallRotation: rotateAngle, //For obstacle to rotate and align with the wall
            };
        });
        const updatedInternalWalls = updatedInternalWalls2.map((wall) => {
            let rotation =
                parseFloat(wall._rotation) -
                parseFloat(rotateAngle) * (180 / Math.PI);
            if (rotation < 0) {
                rotation += 360;
            }
            if (rotation > 360) {
                rotation -= 360;
            }
            const rotationRadians = rotation * (Math.PI / 180);
            const normal = {
                x: Math.cos(rotationRadians),
                y: -Math.sin(rotationRadians),
                z: 0,
            };
            const magnitude = Math.sqrt(
                normal.x * normal.x + normal.y * normal.y,
            );
            normal.x /= magnitude;
            normal.y /= magnitude;
            normal.z = 0;
            return {
                id: wall._id,
                position: [wall._coordinate.x, wall._coordinate.y, 0],
                length: wall._length / SCALEFACTOR_FOR_CONVERSION,
                depth: wall._depth / SCALEFACTOR_FOR_CONVERSION,
                rotation: rotation || 0,
                metadata: wall._metaData,
                normal: normal,
            };
        });

        const updatedWallObj = wallObj.map((wallArray) => {
            if (
                !wallArray ||
                !Array.isArray(wallArray) ||
                wallArray.length === 0
            ) {
                return [];
            }

            return wallArray.map((wall) => {
                const coords =
                    wall._coordinates &&
                    Array.isArray(wall._coordinates) &&
                    wall._coordinates.length >= 2
                        ? wall._coordinates
                        : [
                              { x: 0, y: 0 },
                              { x: 0, y: 0 },
                          ];
                const swingMap = {
                    LEFT: 'Left',
                    RIGHT: 'Right',
                    NONE: 'None',
                };
                const swingDirection = wall._swing
                    ? swingMap[wall._swing] || wall._swing
                    : undefined;

                const openingMap = {
                    INSIDE: 'In',
                    OUTSIDE: 'Out',
                };
                const openingDirection = wall._opening
                    ? openingMap[wall._opening] || wall._opening
                    : undefined;

                const objectTypeMap = {
                    DOOR: 'door',
                    WINDOW: 'window',
                };
                const object = wall._attachmentType
                    ? objectTypeMap[wall._attachmentType] ||
                      wall._attachmentType
                    : undefined;

                const wallTypeMap = {
                    NONE: 'None',
                    SINGLE: 'Single',
                    DOUBLE: 'Double',
                    PATIO: 'Patio',
                    DOORWAY: 'Doorway',
                };
                const type = wall._type
                    ? wallTypeMap[wall._type] || wall._type
                    : undefined;

                return {
                    wallId: wall._wallId,
                    id: wall._id,
                    object: object,
                    type: type,
                    opening: swingDirection,
                    in_out: openingDirection,
                    length:
                        coords[0] && coords[1]
                            ? Utils.calcDistance(coords[0], coords[1]) *
                              SCALEFACTOR_FOR_CONVERSION
                            : 0,
                    width:
                        coords[0] && coords[1]
                            ? Utils.calcDistance(coords[0], coords[1]) *
                              SCALEFACTOR_FOR_CONVERSION
                            : 0,
                    height: 84,
                    position: {
                        x:
                            coords[0] && coords[1]
                                ? (coords[0].x + coords[1].x) / 2
                                : 0,
                        y:
                            coords[0] && coords[1]
                                ? (coords[0].y + coords[1].y) / 2
                                : 0,
                        z: 0,
                    },
                    ground_clearance: 0,
                };
            });
        });

        const handleColorMap = {
            Black: 'black',
            Chrome: 'chrome',
            Brass: 'brass',
            Nickel: 'nickel',
        };
        const handleMaterial = formData.handleColor
            ? handleColorMap[formData.handleColor] || formData.handleColor
            : undefined;

        const rodColor = formData.rodColor
            ? handleColorMap[formData.rodColor] || formData.rodColor
            : undefined;

        const handleStyleMap = {
            Knob: 'knob',
            T_Bar: 'bar',
        };

        const handleStyle = formData.handleStyle
            ? handleStyleMap[formData.handleStyle] || formData.handleStyle
            : undefined;

        const drawerStyleMap = {
            Slab: 'slab',
            Shaker: 'shaker',
        };

        const drawerStyle = formData.drawerStyle
            ? drawerStyleMap[formData.drawerStyle] || formData.drawerStyle
            : undefined;

        const doorStyle = formData.doorStyle
            ? drawerStyleMap[formData.doorStyle] || formData.doorStyle
            : undefined;

        const closetJson = {
            version: '1.1',
            isAutoPopulated: false,
            isFinalized: true,
            points: updatedPoints2,
            finalizedWalls: walls,
            closetType: 'Walk In Closet',
            closetSystem: 'vista',
            modulesData: [],
            closetNormalValue: 1,
            obstacles: updatedObstacles,
            closetId2: id,
            closetName: closet.name,
            drawerColor: formData.drawerColor,
            doorColor: formData.doorColor,
            rodColor: rodColor,
            handleColor: handleMaterial,
            handleStyle: handleStyle,
            drawerStyle: drawerStyle,
            doorStyle: doorStyle,
            color: formData.color,
            wallDecorators: [],
            wallObjects: updatedWallObj,
            legs: formData.legs,
            shelf: formData.shelf,
            topDoors: formData.topDoors,
            bottomDoors: formData.bottomDoors,
            internalWalls: updatedInternalWalls || [],
        };

        const data = JSON.stringify(closetJson, null, 2);
        this._libState.operationsManager.scaleClosets(scale, [closet]);
        this._libState.operationsManager.scaleAttachments(scale);
        this._libState.operationsManager.scaleObstacles(scale);
        this._libState.operationsManager.scaleInternalWalls(scale);
        if (isUpload) {
            const layout = new Blob([data], { type: 'application/json' });
            this.uploadToS3(layout, 'layout.json', 'application/json', id);
        }

        return data;
    };

    getWalls = (points, wallObj, translatedAttachments) => {
        const walls = [];
        const updatedWallObj = wallObj;
        let index = 0;

        for (let i = 0; i < points.length - 1; i++) {
            const wallId = Utils.generateUuid();
            walls.push({
                customId: wallId,
                p1: { x: points[i].x, y: points[i].y },
                p2: { x: points[i + 1].x, y: points[i + 1].y },
                normalValue: 1,
                prevMargin: 0,
                nextMargin: 0,
                subWalls: [],
            });

            translatedAttachments.forEach((attachment) => {
                if (!Array.isArray(attachment?._coordinates)) return;
                if (!attachment._coordinates[0]) return;
                const isPointOnLine = Utils.isPointOnLine(
                    attachment._coordinates[0],
                    { x: points[i].x, y: points[i].y },
                    { x: points[i + 1].x, y: points[i + 1].y },
                );
                const isPointOnLine2 = Utils.isPointOnLine(
                    attachment._coordinates[1],
                    { x: points[i].x, y: points[i].y },
                    { x: points[i + 1].x, y: points[i + 1].y },
                );
                if (!(isPointOnLine && isPointOnLine2)) return;
                attachment._wallId = wallId;
                if (!updatedWallObj[index]) updatedWallObj[index] = [];
                updatedWallObj[index].push(attachment);
            });

            index++;
        }

        // Handle last wall
        const wallId = Utils.generateUuid();
        walls.push({
            customId: wallId,
            p1: {
                x: points[points.length - 1].x,
                y: points[points.length - 1].y,
            },
            p2: { x: points[0].x, y: points[0].y },
            normalValue: 1,
            prevMargin: 0,
            nextMargin: 0,
            subWalls: [],
        });

        translatedAttachments.forEach((attachment) => {
            if (
                attachment &&
                attachment._coordinates &&
                Array.isArray(attachment._coordinates) &&
                attachment._coordinates[0]
            ) {
                if (
                    Utils.isPointOnLine(
                        attachment._coordinates[0],
                        { x: points[0].x, y: points[0].y },
                        {
                            x: points[points.length - 1].x,
                            y: points[points.length - 1].y,
                        },
                    ) &&
                    Utils.isPointOnLine(
                        attachment._coordinates[1],
                        { x: points[0].x, y: points[0].y },
                        {
                            x: points[points.length - 1].x,
                            y: points[points.length - 1].y,
                        },
                    )
                ) {
                    attachment._wallId = wallId;
                    if (!updatedWallObj[index]) updatedWallObj[index] = [];
                    updatedWallObj[index].push(attachment);
                }
            }
        });

        return { walls: walls, wallObj: updatedWallObj };
    };

    predictClosets = async (pdfFile, page) => {
        const formData = new FormData();
        formData.append('pdf', pdfFile);
        formData.append('page', page.toString());
        // try {
        const response = await fetch(
            `${PREDICT_API}/project/${this._libState.viewManager.projectId}/predict`,
            {
                method: 'POST',
                body: formData,
            },
        );

        if (!response.ok) throw new Error('API request failed');
        return await response.json();
        // } catch (error) {
        //     console.error('Error making API request:', error);
        //     message.error('Error making API request. Please try again.');
        //     throw error;
        // }
    };

    uploadToS3 = async (file, filename = 'test', contentType, id) => {
        try {
            // Step 1: Get the presigned URL from the server
            const urlResponse = await fetch(
                `${LAYOUT_API_ENDPOINT}/api/estimating/presignedurl?file_name=${filename}&closet_id=${id}`,
            );

            if (!urlResponse.ok) {
                throw new Error(
                    `Failed to get presigned URL. Status: ${urlResponse.status}`,
                );
            }
            const responseData = await urlResponse.json();
            const signedUrl = responseData.signedUrl;
            // Step 2: Upload the file using the signed URL
            const uploadResponse = await fetch(signedUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': contentType, // Use the content type that matches the pre-signed URL
                },
                body: file,
            });
            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                throw new Error(
                    `Failed to upload file. Status: ${uploadResponse.status}. Response: ${errorText}`,
                );
            }
        } catch (error) {
            console.error('Upload error:', error);
            message.error(error.message);
        }
    };
}
